'use client';

import Loader from '@pickleballinc/features/src/Loader/Loader';
import { notFound, useSearchParams } from 'next/navigation';

import { useGetLeagueDetailsHeaderQueryData } from '@/app/(default)/(leagues)/leagues/[slug]/session/[sessionId]/queries';
import { useGetUserPermissionsApp } from '@/hooks/app/useGetUserPermissionsApp';
import DetailsFooter from '@/modules/league/details/components/DetailsFooter';
import DetailsHeaderWrapper from '@/modules/league/details/components/DetailsHeaderWrapper';
import { RegistrationModals } from '@/modules/league/details/components/RegistrationModals';
import Info from '@/modules/league/details/info/Info';
import Schedule from '@/modules/league/details/schedule/Schedule';
import Sponsors from '@/modules/league/details/sponsors/Sponsors';
import SponsorsHeaderWrapper from '@/modules/league/details/sponsors/SponsorsHeaderWrapper';
import Standings from '@/modules/league/details/standings/Standings';
import { LoaderProvider } from '@/modules/league/PickleballLoaderContext';
import { useAuth } from '@/permissions/AuthProvider';
import { Permission } from '@/permissions/permission';

interface LeagueDetailsWrapperProps {
  params: { slug: string; sessionId: string };
}

export const LeagueDetailsWrapper = ({ params }: LeagueDetailsWrapperProps) => {
  const searchParams = useSearchParams();

  const { slug, sessionId } = params;

  const tab = searchParams?.get('tab') || 'rsvp';

  const { data, isLoading } = useGetLeagueDetailsHeaderQueryData(slug, sessionId);

  const { hasPermission } = useAuth();

  const isPCSuperAdmin = hasPermission(Permission.SUPER_ADMIN);
  const isPBSuperAdmin = hasPermission(Permission.PICKLEBALL_BRACKETS_ADMIN);
  const isManager = useGetUserPermissionsApp(
    data?.leagueId || '',
    data?.userSession?.pbUuid || '',
    ['CanManageScores', 'CanManageLeague', 'CanManageLeagueSession', 'IsPrimary']
  );

  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <Loader backgroundColor='bg-leagues-300/90' />
      </div>
    );
  }

  if (!data) {
    return notFound();
  }

  const isAuthorized = isPCSuperAdmin || isManager || isPBSuperAdmin;
  const isSuperAdmin = isPCSuperAdmin || isPBSuperAdmin;

  const showSponsors = data.sponsors.sponsorsGrouped && data.sponsors.sponsorsGrouped.length > 0;

  return (
    <LoaderProvider>
      <div className='container xl:px-0 mx-auto flex flex-wrap'>
        {tab === 'sponsors' && showSponsors ? (
          <SponsorsHeaderWrapper header={data.header} params={params} showSponsors={showSponsors} />
        ) : (
          <>
            <DetailsHeaderWrapper
              header={data.header}
              params={params}
              showSponsors={showSponsors}
              isAuthorized={isAuthorized}
              isSuperAdmin={isSuperAdmin}
              user={data.userSession}
            />
            <div className='mb-2 mt-4 w-full border-b border-solid border-border-500' />
          </>
        )}

        {tab === 'standings' && <Standings session={data.header.session} params={params} />}

        {tab === 'schedule' && (
          <Schedule
            session={data.header.session}
            location={data.header.locationInfo.locationInfo}
            params={params}
          />
        )}

        {tab === 'info' && (
          <Info
            details={data.header.details}
            session={data.header.session}
            location={data.header.locationInfo.locationInfo}
          />
        )}

        {tab === 'sponsors' && (
          <Sponsors
            leagueId={data.header.details.LeagueID}
            clubId={data.header.details.ClubID}
            sponsorGroups={data.sponsors.sponsorsGrouped}
          />
        )}
        {tab !== 'sponsors' && (
          <DetailsFooter
            session={data.header.session}
            location={data.header.locationInfo.locationInfo}
            params={params}
          />
        )}

        <RegistrationModals
          params={params}
          user={data.userSession}
          details={data.header.details}
          session={data.header.session}
        />
      </div>
    </LoaderProvider>
  );
};
